import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { useAuth0 } from '../../components/auth/DevAuth0Provider';
import useDataAccess from '../../hooks/useDataAccess';
import { pageCategories } from '../../utils/navigationConfig';
import { createAccount } from '../../api/ticketData';

/**
 * Customer Bootstrap Page
 * 
 * Allows Helios admins to create new customers with complete Auth0 setup
 */
const CustomerBootstrap = () => {
  const user = useAuthUser();
  const { getAccessTokenSilently } = useAuth0();
  const { makeCustomerApiRequest } = useDataAccess();

  const [animationStates, setAnimationStates] = useState({
    header: false,
    auth0: false,
    form: false,
  });

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    clientId: '',
    category: '',
    chatEndpoint: '',
    adminEmail: '',
    adminName: '',
    config: {
      ai_assistant_name: '',
      business_type: '',
      features: []
    }
  });

  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templates, setTemplates] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [isTestingAuth0, setIsTestingAuth0] = useState(false);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const timeouts = [];
    
    // Stagger the animations with delays - customize timing as needed
    timeouts.push(setTimeout(() => {
      setAnimationStates(prev => ({ ...prev, header: true }));
    }, 100));
    
    timeouts.push(setTimeout(() => { 
      setAnimationStates(prev => ({ ...prev, auth0: true }));
    }, 300));
    timeouts.push(setTimeout(() => { 
      setAnimationStates(prev => ({ ...prev, form: true }));
    }, 500));
  
    // Cleanup timeouts on unmount
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const loadTemplates = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
        scope: 'openid profile email'
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-bootstrap/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  // Auto-generate client ID and chat endpoint when customer ID changes
  useEffect(() => {
    if (formData.id && !formData.clientId) {
      setFormData(prev => ({
        ...prev,
        clientId: prev.id,
        chatEndpoint: `/${prev.id}/chat`
      }));
    }
  }, [formData.id]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
  };

  // const loadTemplate = (templateKey) => {
  //   const template = templates[templateKey];
  //   if (template) {
  //     setFormData({
  //       ...template
  //     });
  //   }
  // };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Only PNG, JPG, SVG, and GIF files are allowed.');
        return;
      }

      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('File too large. Maximum size is 2MB.');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);

      setError(null);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.id) errors.id = 'Customer ID is required';
    else if (!/^[a-zA-Z0-9_-]+$/.test(formData.id)) errors.id = 'Customer ID must be alphanumeric with optional hyphens/underscores';
    else if (formData.id.length < 2 || formData.id.length > 50) errors.id = 'Customer ID must be 2-50 characters';

    if (!formData.name) errors.name = 'Customer name is required';
    if (!formData.clientId) errors.clientId = 'Client ID is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.adminEmail) errors.adminEmail = 'Admin email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) errors.adminEmail = 'Invalid email format';
    if (!formData.adminName) errors.adminName = 'Admin name is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the validation errors');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getAccessTokenSilently({
        audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
        scope: 'openid profile email'
      });

      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add form fields
      Object.keys(formData).forEach(key => {
        if (key === 'config') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add logo file if selected
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-bootstrap/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok) {
        // Customer created successfully, now create the Zoho Desk account
        try {
          console.log('Creating Zoho Desk account for customer:', formData.name);
          
          const accountData = {
            accountName: formData.name
          };
          
          const accountResult = await createAccount(accountData);
          console.log('Zoho Desk account created successfully:', accountResult);
          
          // Add account creation info to success message
          const enhancedResult = {
            ...result,
            message: result.message + ' Zoho Desk account also created successfully.',
            zohoAccount: accountResult
          };
          
          setSuccess(enhancedResult);
        } catch (accountError) {
          console.error('Failed to create Zoho Desk account:', accountError);
          
          // Customer was created but account creation failed
          const warningResult = {
            ...result,
            message: result.message + ' Warning: Zoho Desk account creation failed - you may need to create it manually.',
            warning: 'Zoho Desk account creation failed: ' + (accountError.message || 'Unknown error')
          };
          
          setSuccess(warningResult);
        }
        
        // Reset form
        setFormData({
          id: '',
          name: '',
          clientId: '',
          chatEndpoint: '',
          adminEmail: '',
          adminName: '',
          config: {
            ai_assistant_name: '',
            business_type: '',
            features: []
          }
        });
        setLogoFile(null);
        setLogoPreview(null);
      } else {
        setError(result.details || result.error || 'Customer creation failed');
      }
    } catch (error) {
      console.error('Customer bootstrap error:', error);
      setError('Network error: Failed to create customer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      id: '',
      name: '',
      clientId: '',
      category: '',
      chatEndpoint: '',
      adminEmail: '',
      adminName: '',
      config: {
        ai_assistant_name: '',
        business_type: '',
        features: []
      }
    });
    setLogoFile(null);
    setLogoPreview(null);
    setValidationErrors({});
    setError(null);
    setSuccess(null);
  };

  const testAuth0Connection = async () => {
    setIsTestingAuth0(true);
    setTestResults(null);
    setError(null);

    try {
      const token = await getAccessTokenSilently({
        audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
        scope: 'openid profile email'
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-bootstrap/test-auth0`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      setTestResults(result);

      if (!response.ok) {
        setError(`Auth0 Test Failed: ${result.details || result.error}`);
      }
    } catch (error) {
      console.error('Auth0 test error:', error);
      setError(`Auth0 Test Error: ${error.message}`);
    } finally {
      setIsTestingAuth0(false);
    }
  };

  const setupAuth0Roles = async () => {
    setIsTestingAuth0(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently({
        audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
        scope: 'openid profile email'
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-bootstrap/setup-roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(result);
        // Refresh test results
        await testAuth0Connection();
      } else {
        setError(`Role Setup Failed: ${result.details || result.error}`);
      }
    } catch (error) {
      console.error('Role setup error:', error);
      setError(`Role Setup Error: ${error.message}`);
    } finally {
      setIsTestingAuth0(false);
    }
  };

  // Only show for Helios admins
  if (!user?.isHeliosAdmin) {
    return (
      <div className="responsive-page-layout">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon icon="mdi:lock" className="text-6xl text-accent mb-4" />
            <h3 className="text-xl text-primary mb-2">Access Denied</h3>
            <p className="text-accent">Only Helios admins can access customer bootstrap.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-page-layout w-full scrollable h-full">
      <div className="flex flex-col gap-6 w-full h-full">
        {/* Header */}
        <div className={`flex items-start justify-between w-full transition-all duration-700 ease-out-in ${
            animationStates.header
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
        }`}>
          <div className='flex flex-col items-start gap-2'>
            <div className="header-textbox">
                <Icon icon="mdi:domain-plus" className="header-icon" />
                Customer Bootstrap
            </div>
            <p className="text-font-secondary">Create a new customer with complete Auth0 setup</p>
          </div>
        </div>

        {/* Success Message */}
        {/* {success && (
          <div className={`border p-4 rounded-md ${success.warning ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
            <div className="flex items-center gap-2">
              <Icon icon={success.warning ? "mdi:alert-circle" : "mdi:check-circle"} 
                    className={`text-xl ${success.warning ? 'text-yellow-600' : 'text-green-600'}`} />
              <div>
                <h4 className={`font-medium ${success.warning ? 'text-yellow-800' : 'text-green-800'}`}>
                  Customer Created Successfully!
                </h4>
                <p className={`text-sm mt-1 ${success.warning ? 'text-yellow-700' : 'text-green-700'}`}>
                  {success.message}
                </p>
                <div className={`mt-2 text-xs ${success.warning ? 'text-yellow-600' : 'text-green-600'}`}>
                  <p>Organization ID: {success.organization?.id}</p>
                  <p>Admin User ID: {success.adminUser?.user_id}</p>
                  {success.zohoAccount && (
                    <p>Zoho Account ID: {success.zohoAccount.id || success.zohoAccount.accountId}</p>
                  )}
                </div>
                {success.warning && (
                  <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 p-2 rounded">
                    <strong>Warning:</strong> {success.warning}
                  </div>
                )}
              </div>
            </div>
          </div>
        )} */}

        {/* Error Message */}
        {/* {error && (
          <div className="bg-red-50 border border-red-300 p-4 rounded-md">
            <div className="flex items-center gap-2">
              <Icon icon="mdi:alert-circle" className="text-error text-xl" />
              <div>
                <h4 className="font-medium text-red-800">Error</h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )} */}

        {/* Auth0 Test Results */}
        {/* {testResults && (
          <div className={`p-4 rounded-md bg-background shadow-neo-page transition-all duration-1000 ease-out-in ${
              animationStates.auth0
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
          }}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon icon={testResults.success ? "mdi:check-circle" : "mdi:alert-circle"}
                    className={`text-2xl ${testResults.success ? 'text-primary' : 'text-error'}`} />
              <h4 className={`font-medium`}>
                Auth0 Connection Test
              </h4>
            </div>

            {testResults.success ? (
              <div className="text-sm space-y-2 w-full">
                <div className='flex flex-row items-center justify-between'>
                  <p className="text-primary flex flex-row gap-1 items-center"><Icon icon='material-symbols:check-box-rounded' className='text-xl' /> Auth0 connection working</p>
                  <p className="text-primary flex flex-row gap-1 items-center"><Icon icon='material-symbols:check-box-rounded' className='text-xl' /> Found {testResults.roles_count} roles</p>
                  <p className="text-primary flex flex-row gap-1 items-center"><Icon icon='material-symbols:check-box-rounded' className='text-xl' /> Found {testResults.organizations_count} organizations</p>
                </div>

                {testResults.missing_roles && testResults.missing_roles.length > 0 && (
                  <div className="p-3 rounded">
                    <p className="text-yellow-800 font-medium">⚠️ Missing Required Roles:</p>
                    <ul className="text-yellow-700 text-xs mt-1">
                      {testResults.missing_roles.map(role => (
                        <li key={role}>• {role}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <details className="flex flex-col items-start gap-2 w-full">
                  <summary className="cursor-pointer text-primary">View Details</summary>
                  <div className="text-xs bg-background shadow-neo-page p-3 rounded-md w-full flex flex-col items-start gap-2 w-full">
                    <p><strong>Available Roles:</strong> {testResults.available_roles?.map(r => r.name).join(', ')}</p>
                    <p><strong>User:</strong> {testResults.user_info?.email}</p>
                    <p><strong>Is Helios Admin:</strong> {testResults.user_info?.isHeliosAdmin ? 'Yes' : 'No'}</p>
                  </div>
                </details>
              </div>
            ) : (
              <p className="text-red-700 text-sm">{testResults.details}</p>
            )}
          </div>
        )} */}

        {/* Auth0 Test Controls */}
        {/* <div className={`bg-background shadow-neo-page rounded-md p-4 flex flex-row gap-4 items-center justify-between transition-all duration-1000 ease-out-in ${
            animationStates.auth0
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
        }}`}>
          <div className='flex flex-col items-start gap-2'>
            <h3 className="font-medium flex items-center gap-2">
              <Icon icon="mdi:test-tube" className='text-xl text-blue-600' />
              Auth0 Diagnostics
            </h3>
            <p className="text-blue-600 text-xs">
              Run this test first to ensure Auth0 is properly configured before creating customers.
            </p>
          </div>
          <div className="flex">
            <button
              onClick={testAuth0Connection}
              disabled={isTestingAuth0}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isTestingAuth0 ? (
                <>
                  <Icon icon="eos-icons:loading" className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Icon icon="mdi:connection" />
                  Test Auth0 Connection
                </>
              )}
            </button>
            {testResults?.missing_roles && testResults.missing_roles.length > 0 && (
              <button
                onClick={setupAuth0Roles}
                disabled={isTestingAuth0}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isTestingAuth0 ? (
                  <>
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:account-plus" />
                    Create Missing Roles
                  </>
                )}
              </button>
            )}
          </div>
        </div> */}

        {/* Bootstrap Form */}
        <form onSubmit={handleSubmit} className={`transition-all duration-1000 ease-out-in flex flex-col justify-between h-full ${
            animationStates.form
                ? 'opacity-100 translate-y-0 scale-100' 
            :     'opacity-0 translate-y-4 scale-95'
        }}`}>
          <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Basic Information */}
                <div className="flex flex-col gap-4">
                  <div className='flex flex-col md:flex-row gap-2 md:gap-0 items-start md:items-center justify-between'>
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Icon icon="mdi:information" className='text-primary text-xl' />
                      Basic Information
                    </h3>
                    <p className="text-xs text-font-secondary">Admin will receive an email to set their password</p>
                  </div>
                  <div className="input-container">
                    <label className="">
                      Customer ID <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => handleInputChange('id', e.target.value)}
                      className={`w-full input-textbox rounded-md ${
                        validationErrors.id ? 'border-error' : 'border-neo-border'
                      }`}
                      placeholder="e.g., acme-corp"
                    />
                    {validationErrors.id && (
                      <p className="text-error text-xs mt-2">{validationErrors.id}</p>
                    )}
                  </div>

                  <div className="input-container">
                    <label className="">
                      Customer Name <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full input-textbox rounded-md ${
                        validationErrors.name ? 'border-red-500' : 'border-neo-border'
                      }`}
                      placeholder="e.g., ACME Corporation"
                    />
                    {validationErrors.name && (
                      <p className="text-error text-xs mt-2">{validationErrors.name}</p>
                    )}
                  </div>

                  <div className="input-container">
                    <label className="">
                      Client ID <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.clientId}
                      onChange={(e) => handleInputChange('clientId', e.target.value)}
                      className={`w-full input-textbox rounded-md ${
                        validationErrors.clientId ? 'border-red-500' : 'border-neo-border'
                      }`}
                      placeholder="e.g., acme"
                    />
                    {validationErrors.clientId && (
                      <p className="text-error text-xs mt-2">{validationErrors.clientId}</p>
                    )}
                    <p className="text-xs text-font-secondary mt-2">Used for API routing and chat endpoints</p>
                  </div>

                  <div className="input-container">
                    <label className="">
                      Category <span className="text-error">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className={`w-full input-textbox rounded-md ${
                        validationErrors.category ? 'border-red-500' : 'border-neo-border'
                      }`}
                    >
                      <option value="">Select a Category...</option>
                      {/* Filter out ADMIN and COMMON as they are not assignable categories */}
                      {Object.keys(pageCategories)
                        .filter(key => pageCategories[key] !== 'admin' && pageCategories[key] !== 'common')
                        .map(key => (
                          <option key={pageCategories[key]} value={pageCategories[key]}>
                            {key}
                          </option>
                      ))}
                    </select>
                    {validationErrors.category && (
                      <p className="text-error text-xs mt-2">{validationErrors.category}</p>
                    )}
                  </div>

                  <div className="input-container">
                    <label className="">
                      Chat Endpoint
                    </label>
                    <input
                      type="text"
                      value={formData.chatEndpoint}
                      onChange={(e) => handleInputChange('chatEndpoint', e.target.value)}
                      className="w-full input-textbox rounded-md"
                      placeholder="Auto-generated from Client ID"
                    />
                    <p className="text-xs text-font-secondary mt-2">Leave blank to auto-generate: /{formData.clientId}/chat</p>
                  </div>
                </div>

                {/* Admin User */}
                <div className="flex flex-col gap-4 card-container bg-background rounded-md p-3 md:p-4">
                  <h3 className="text-lg font-medium  flex items-center gap-2">
                    <Icon icon="mdi:account-supervisor" className='text-primary text-xl' />
                    Admin User
                  </h3>

                  <div className='flex flex-col gap-2 sm:gap-4'>
                    <div className="input-container">
                      <label className="">
                        Admin Email <span className="text-error">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.adminEmail}
                        onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                        className={`w-full input-textbox rounded-md ${
                          validationErrors.adminEmail ? 'border-red-500' : 'border-neo-border'
                        }`}
                        placeholder="admin@customer.com"
                      />
                      {validationErrors.adminEmail && (
                        <p className="text-error text-xs mt-2">{validationErrors.adminEmail}</p>
                      )}
                    </div>

                    <div className="input-container">
                      <label className="">
                        Admin Name <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.adminName}
                        onChange={(e) => handleInputChange('adminName', e.target.value)}
                        className={`w-full input-textbox rounded-md ${
                          validationErrors.adminName ? 'border-red-500' : 'border-neo-border'
                        }`}
                        placeholder="Admin User"
                      />
                      {validationErrors.adminName && (
                        <p className="text-error text-xs mt-2">{validationErrors.adminName}</p>
                      )}
                    </div>

                    <div className="input-container">
                      <label className="">
                        Admin Password <span className="text-error">*</span>
                      </label>
                      <input
                        type="password"
                        value={formData.adminPassword}
                        onChange={(e) => handleInputChange('adminPassword', e.target.value)}
                        className="w-full input-textbox rounded-md"
                        placeholder="Leave blank to auto-generate"
                      />
                      <p className="text-xs text-font-secondary mt-2">If blank, a secure password will be generated</p>
                    </div>
                    <div className="input-container">
                      <label className="">
                        Organization Logo
                      </label>
                      <div className="flex flex-col gap-3">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/gif"
                          onChange={handleLogoChange}
                          className="w-full input-textbox rounded-md"
                        />
                        <p className="text-xs text-font-secondary">Optional. PNG, JPG, SVG, or GIF. Max 2MB.</p>

                        {logoPreview && (
                          <div className="flex items-center gap-3 p-3 bg-bgSecondary shadow-neo-card-inner rounded-md">
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="w-16 h-16 object-contain rounded-md shadow-neo-inset"
                            />
                            <div className="flex-1 gap-1 flex flex-col">
                              <p className="text-base font-medium">{logoFile?.name}</p>
                              <p className="text-sm text-accent">{(logoFile?.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button
                              type="button"
                              onClick={removeLogo}
                              className="text-error hover:text-red-700 p-1"
                            >
                              <Icon icon="mdi:close" className="text-lg" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Configuration */}
              <div className="">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-accent hover:text-primary transition-colors"
                >
                  <Icon icon={showAdvanced ? "mdi:chevron-down" : "mdi:chevron-right"} />
                  Advanced Configuration
                </button>

                {showAdvanced && (
                  <div className="mt-4 rounded">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="input-container">
                        <label className="">
                          AI Assistant Name
                        </label>
                        <input
                          type="text"
                          value={formData.config.ai_assistant_name}
                          onChange={(e) => handleConfigChange('ai_assistant_name', e.target.value)}
                          className="w-full input-textbox rounded-md"
                          placeholder="e.g., Assistant"
                        />
                      </div>

                      <div className="input-container">
                        <label className="">
                          Business Type
                        </label>
                        <input
                          type="text"
                          value={formData.config.business_type}
                          onChange={(e) => handleConfigChange('business_type', e.target.value)}
                          className="w-full input-textbox rounded-md"
                          placeholder="e.g., Professional Services"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </div>
          {/* Form Actions */}
          <div className="flex items-center justify-between py-3 my-4 border-t-2 border-bgAccent">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 flex items-center gap-1 button-container text-font-primary hover:text-error transition-colors"
              disabled={isLoading}
            >
              <Icon icon="mdi:restart" className='text-xl text-error' />
              Reset Form
            </button>

            <button
              type="submit"
              disabled={isLoading}
              className="p-3 button-container hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Icon icon="eos-icons:loading" className="animate-spin" />
                  Creating Customer...
                </>
              ) : (
                <>
                  <Icon icon="mdi:domain-plus" className='text-xl text-primary' />
                  Create Customer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerBootstrap;
